/**
 * Classe pai com todos os métodos e atributos em comum entre os gerenciadores de temas
 */
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSeries } from 'src/hooks/Data';
import { dataToColors, getChartThemeColors } from 'src/util/colors';
import getChartActionByCardType from './actionResolver';
import calculateChartColorsArray from './calculateChartColorsArray';
import { BAR, HORIZONTAL_STACKED, PIE, PIE_GRADE, RADAR, VERTICAL_STACKED } from 'src/components/Charts/types';
import { EVOLUTIVE_CARD, EVOLUTIVE_CROSS_CARD } from 'src/sections/GroupCards/cardTypes';

/**
 * @param analyzeId - Id da configuração de um gráfico ou id da pergunta.
 * @param surveyId - Id da pesquisa atualmente selecionada.
 * @param withData . Booleano de verificação se existem dados plotados no gráfico
 * @param chartRef - Referência para o gráfico.
 * @param appTheme - Objeto atual do tema da aplicação.
 * @param chartType - Tipo atual do gráfico selecionado.
 */
export default class ThemeManager {
  constructor(
    analyzeId,
    surveyId,
    withData,
    chartRef,
    appTheme,
    chartType,
    cardType,
    consolidations = [],
    chartIsReversed = false
  ) {
    this._analyzeId = analyzeId;
    this._surveyId = surveyId;
    this._withData = withData;
    this._chartRef = chartRef;
    this._appTheme = appTheme;
    this._chartType = chartType;
    this._cardType = cardType;
    this._chartIsReversed = chartIsReversed;

    // Só queremos as consolidações que possuem mais de uma alternativa, pois somente esses casos
    // nos interessa para a regra de consumo de cor para consolidações
    this._consolidations = consolidations;

    // Event Handlers - São eventos que serão setados por quem extende a classe principal (ThemeManager)
    this.eventHandlers = {
      // Caso esteja setado, a callback será chamada quando ocorrer uma mudança de cor
      onColorChanged: null,
      // Caso esteja setado, a callback será chamada quando o gráfico for inicializado pela primeira vez
      onChartInitialized: null,
    };

    // Estado de inicialização do gráfico, será setado para true quando o gráfico for montado
    // e false quando não existirem dados plotados.
    const [chartInitialized, setChartInitialized] = useState(false);
    // Estado que armazena o array de cores consumido pelos gráficos
    const [colors, setColors] = useState([]);

    // Externalizando os estados para a instância
    this.calculatedChartColorArray = colors;
    this.setColors = setColors;

    this._chartInitialized = chartInitialized;
    this._setChartInitialized = setChartInitialized;
  }

  /**
   * Seta um estado de inicialização para o gráfico, representado em "chartInitialized", será falso
   * quando o gráfico atual não tiver sido renderizado, e true quando sua renderização estiver pronta.
   */
  _setChartInitializationState() {
    useEffect(() => {
      // Verifica se o gráfico possui dados plotados
      const hasPlottedData = this._hasPlottedData();
      // Caso tenha dados plotados porém o estado de inicialização ainda é falso:
      if (hasPlottedData && !this._chartInitialized) {
        // Recupera a callback de inicialização
        const { onChartInitialized } = this.eventHandlers;
        // Set de estados em seus valores iniciais
        this._setChartInitialized(true);
        this.setColors([]);

        if (typeof onChartInitialized === 'function') {
          onChartInitialized();
        }
      }
      // Reseta o estado de inicialização quando o gráfico não tiver dados plotados
      else if (!this._withData && this._chartInitialized) {
        this._setChartInitialized(false);
      }
    }, [
      this._withData,
      this._analyzeId,
      this._chartSeries,
      this._chartInitialized,
    ]);
  }

  /**
   * Calcula e aplica as cores que devem ser consumidas pelo gráfico após o seu
   * estado de inicialização ser verdadeiro.
   * @param {boolean} checkColorConfigs Caso seja true, uma verificação a mais é feita, checando
   * se o gráfico além de ter inicializado, possui configurações de cores prévias.
   */
  _setChartColorsOnInit(checkColorConfigs = false) {
    const rules = !checkColorConfigs
      ? this._chartInitialized
      : this._chartInitialized && this._hasColorConfigs();

    useEffect(() => {
      if (rules) {
        this._recalculateColorArray({ ...this._payload.chartsColors });
      }
    }, [
      this._payload.chartsColors,
      this._chartInitialized,
      this._appTheme.title,
      this._chartSeries,
      this._consolidations,
    ]);
  }

  /**
   * Seta a callback a ser disparada pós algum evento de inicialização do gráfico.
   * @param {Function} eventHandler
   */
  setOnChartInitializedEventHandler(eventHandler) {
    if (typeof eventHandler === 'function') {
      this.eventHandlers.onChartInitialized = eventHandler;
    }
  }

  /**
   * Seta a callback a ser disparada pós algum evento de mudança de cores.
   * @param {Function} eventHandler
   */
  setColorChangedEventHandler(eventHandler) {
    if (typeof eventHandler === 'function') {
      this.eventHandlers.onColorChanged = eventHandler;
    }
  }

  /**
   * O método seta as configurações do gráfico (cores, fontes, esquema de cores)
   * @param {string} redux_config_key Chave do estado do redux relacionada à configuração de um gráfico. e.x: multiCrossChartConfigData
   */
  _setChartConfiguration(redux_config_key) {
    const configData = useSelector((state) => state.charts[redux_config_key]);

    const { payload, apiHasNoConfigs, colorPickerColors, error } = configData;

    this._payload = payload;
    this._apiHasNoConfigs = apiHasNoConfigs;
    this._colorPickerColors = colorPickerColors;
    this._error = error;
    this._dispatch = useDispatch();

    this._setValidConsolidations(this._payload.chartsColors);
  }

  // Chama o hook useSeries para armazenar os dados plotados no gráfico na ordem correta
  // de renderização.
  _setChartSeries() {
    this._chartSeries = useSeries(
      this._chartRef,
      this._chartType,
      this._cardType
    );
  }

  /**
   * Recalcula as cores que serão consumidas pelo gráfico.
   * @param {object} colorsPayload Configuração de cores de referência para o recálculo
   */
  _recalculateColorArray(colorsPayload = null) {
    if (colorsPayload) {
      // Calcula as cores do gráfico levando em consideração os dados de tema da aplicação
      // as series de dados do gráfico, o tipo do gráfico e o payload de cores recebido.
      let calculatedColors = calculateChartColorsArray({
        data: this._chartSeries,
        theme: this._appTheme,
        chartType: this._chartType,
        cardType: this._cardType,
        chartsColors: this.withConsolidations({ ...colorsPayload }),
        chartIsReversed: this._chartIsReversed
      });

      // Atualiza o estado das cores consumidas pelo gráfico apenas se uma mudança tiver ocorrido.
      if (
        calculatedColors.toString() !==
        this.calculatedChartColorArray.toString()
      ) {
        this.setColors([...calculatedColors]);
      }

      const { onColorChanged } = this.eventHandlers;

      // Dispara a callback, caso exista, indicando que houve um evento de mudança de cor.
      if (typeof onColorChanged === 'function') {
        onColorChanged([...calculatedColors]);
      }

    }
  }

  /**
   *
   * @returns {boolean} true caso contenha dados plotados no gráfico, false caso contrário.
   */
  _hasPlottedData() {
    return (
      this._analyzeId &&
      this._analyzeId >= 0 &&
      this._chartSeries &&
      this._chartSeries.length &&
      this._withData
    );
  }

  _hasColorConfigs() {
    return !!Object.keys(this._payload.chartsColors).length;
  }

  // Define os métodos de interação do usuário com o gráfico (atualizar cores, atualizar fontes, etc)
  _setActions() {
    this.updateChartColors = this._updateChartColors.bind(this);

    this.updateColorScheme = this.updateColorScheme.bind(this);
    this.updateFontSize = this.updateFontSize.bind(this);

    this.updateChartColors = this.updateChartColors();
  }

  isGradeChartType() {
    return [
      HORIZONTAL_STACKED,
      VERTICAL_STACKED,
      RADAR,
      PIE_GRADE
    ].includes(this._chartType);
  }

  /**
   * Caso um payload de armazenamento de cores de um gráfico
   * de grade siga o padrão { [linha$$$$coluna]: cor }, então resolvemos
   * este payload para o formato previamente utilizado { [coluna]: cor },
   * uma vez que não precisamos de uma referência de cor para cada alternativa 
   * da grade.
   * @param {*} configuracaoAtual Configuração de cores atual
   * @param {*} enunciadoAlterado Nome da coluna requisitado para alteração de cor
   * @param {*} novaCor Cor a ser inserida
   */
  _resolveGradeColorUpdate(configuracaoAtual, enunciadoAlterado, novaCor) {
    const configuracao = { ...configuracaoAtual };

    const coresPadraoSalvo = Object.keys(configuracao)
      .filter(key => key.includes('$$$$'))
      
    let key;

    for (let i = 0; i < coresPadraoSalvo.length; i ++) {
      key = coresPadraoSalvo[i];
      
      delete configuracao[key];
    }

    configuracao[enunciadoAlterado] = novaCor;
    
    return configuracao;
  }

  // Callback enviando para o componente de chart para atualizar as cores de configuração do chart
  // na API
  _updateChartColors() {
    return useCallback(
      (enunciadoAlterado, newColor) => {
        const { updateChartColorsAction } = getChartActionByCardType(
          this._cardType
        );

        const chartConfigDataPayload = {
          ...this._payload,
        };

        // Atualiza as cores alteradas ao objeto de cores baseado no tipo de gráfico atual
        // O gráfico de barras horizontais possui uma configuração de cores diferente dos demais, por isso
        // uma lógica única foi desenvolvido para ele.
        
        if (!this.isGradeChartType()) {
          chartConfigDataPayload.chartsColors[enunciadoAlterado] = newColor;
        } else {
          chartConfigDataPayload.chartsColors = this._resolveGradeColorUpdate(
            chartConfigDataPayload.chartsColors, 
            enunciadoAlterado,
            newColor
          );
        }

        // Copia o payload carregado da API com objeto de configuração de cores e fontes dos gráficos
        // Submete esta mudança para a API
        updateChartColorsAction(
          this._surveyId,
          this._analyzeId,
          chartConfigDataPayload,
          this._colorPickerColors,
          newColor,
          this._apiHasNoConfigs,
          this._dispatch,
          // Success callback (on 200 status code): Aqui o recálculo de cores só ocorre
          // caso a requisição tenha retornado status code 200, senão, o recálculo não
          // será aplicado, e as cores voltarão ao seu estado antigo.
          () =>
            this._recalculateColorArray({
              ...chartConfigDataPayload.chartsColors,
            })
        );
      },
      [
        this._payload,
        this._colorPickerColors,
        this._dispatch,
        this._analyzeId,
        this._surveyId,
        this._apiHasNoConfigs,
        this._chartSeries,
        this._appTheme.title,
        this._consolidations,
        this._chartIsReversed
      ]
    );
  }

  // Atualiza o tamanho da fonte de um gráfico
  updateFontSize(newFontSizeValue) {
    const { updateFontSizeAction } = getChartActionByCardType(this._cardType);

    const newConfigData = {
      ...this._payload,
      fontsSizes: newFontSizeValue,
    };

    updateFontSizeAction(
      this._surveyId,
      this._analyzeId,
      newFontSizeValue,
      newConfigData,
      this._apiHasNoConfigs,
      this._dispatch
    );
  }
  
  // Atualiza o esquema de cores de um gráfico
  updateColorScheme(newColorSchemeValue) {
    // Retorna o array de cores baseado no tema selecionado no menu meatball (neutro|padrão)
    // A lógica de cálculo de dados quando o gráfico é do tipo grade é diferente
    // portanto, chartSeries é passado, que é o array de dados com todo o cálculo
    // pré-pronto.
    const newData = [...this._chartSeries];
    const colorSchemeColors = getChartThemeColors(
      this._appTheme.colors.charts,
      newColorSchemeValue
    );
    
    /**
     * Sim, essa solução é uma porcaria, porém esse código já está demasiadamente complexo,
     * então por hora, para tratar a renderização na ordem correta de cores do tema neutro,
     * apenas verificamos quais contextos são elegíveis de reordenação do array de "series".
     * 
     * Por que isso foi necessário? 
     * 
     * R: Dependendo do tipo de gráfico, suas series são armazenadas de forma sequencial ou invertida.
     * No caso do gráfico de barras e de pizza, são invertidos, gerando um side effect onde 
     * a ordem das cores do tema neutro são salvas com essa inversão indevida.
     * 
     * Então por exemplo, se esperávamos que a ordem de exibição do tema neutro fosse, nesta ordem, 
     * corA, corB, e corC, o que é renderizado nesses casos seria corC, corB, corA.
     * 
     * Tudo isso só é necessário devido à lógica de inserção das cores com base no enunciado da alternativa, implementada em
     * "dataToColors(series, cores_do_tema)"
     */
    if (!(this.isEvolutiveCard() || this.isEvolutiveCrossCard()) && this.isPieChart()) {
      newData.reverse();
    }
  
    const newPayload = {
      ...this._payload,
      colorSchemeValue: newColorSchemeValue,
      chartsColors:
        dataToColors(newData, colorSchemeColors) || this._payload.chartsColors,
    };

    const { updateColorSchemeAction } = getChartActionByCardType(
      this._cardType
    );

    updateColorSchemeAction(
      this._surveyId,
      this._analyzeId,
      newPayload,
      newColorSchemeValue,
      this._apiHasNoConfigs,
      this._dispatch
    );
  }

  // Recupera as configurações de configurações do gráfico
  getConfig() {
    return {
      chartsColors: this._payload.chartsColors,
      fontsSizes: this._payload.fontsSizes,
      colorSchemeValue: this._payload.colorSchemeValue,
      colorPickerColors: this._colorPickerColors,
      configurationError: this._error,
    };
  }

  // Consolidações

  /**
   * Filtra as consolidações considerando que não existe nenhuma consolidação que pertença
   * às legendas do payload de cores do gráfico. Pois isto infere que o item consolidado
   * no gráfico não tem configuração de cor. E só queremos aplicar a regra de cor por percentual
   * (veja withConsolidations) caso essa condição seja verdadeira.
   *
   * @param {object} colorsPayload Objeto de cores do gráfico atual
   */
  _setValidConsolidations(colorsPayload) {
    const colorPayloadKeys = Object.keys(colorsPayload);
    if (!!this._consolidations.length && !!colorPayloadKeys.length) {
      this._consolidations = this._consolidations.filter(
        ({ title: enunciadoConsolidacao }) =>
          !colorPayloadKeys.includes(enunciadoConsolidacao)
      );
    }
  }

  /**
   * Dada a regra de negócio
   * "R06: Se consolidar alternativas, que provavelmente tem cores diferentes, a cor que prevalecerá na nova legenda e parte do gráfico, será a que antes tinha o maior percentual"
   * Foi implementado esse algorítmo para tratar dessa regra.
   *
   * Referência: https://www.notion.so/alfa-group/Consolida-o-de-alternativas-Gr-fico-simples-f047f645c3ca4a9faec5493125d6049e
   *
   * @param {object} colorsPayload Objeto de cores do gráfico atual
   * @returns Objeto de cores com a lógica de consolidação aplicada | objeto sem modificação.
   */
  withConsolidations(colorsPayload) {
    // Recuperamos os enunciados do objeto atual de cores
    const colorPayloadKeys = Object.keys(colorsPayload);

    if (!!this._consolidations.length && !!colorPayloadKeys.length) {
      const colorsCopy = { ...colorsPayload };

      const highestPercentualMeta = {
        total: 0,
        ref: null,
      };

      for (let i = 0; i < this._consolidations.length; i++) {
        const { alternatives, title } = this._consolidations[i];

        // Recuperamos apenas os enunciados das alternativas que pertencem à consolidação iterada
        const enunciadosInCons = alternatives.map(({ title, resultado_percentual }) => {
          // A primeira verificação infere que só estamos interessados nos enunciados das alternativas
          // consolidadas que pertencem ao objeto de cores.
          // O intúito dessa lógica é recuperar o enunciado da alternativa consolidada que possui o maior
          // valor percentual, dada a regra de negócio de cores.
          if (colorPayloadKeys.includes(title) && resultado_percentual >= highestPercentualMeta.total) {
            highestPercentualMeta.total = resultado_percentual;
            highestPercentualMeta.ref = title;
          }
          return title;
        });

        // Identificamos quais enunciados fazem parte da consolidação atual
        const currentConsEnunciados = enunciadosInCons.filter((enunc) =>
          colorPayloadKeys.includes(enunc)
        );

        if (currentConsEnunciados.length) {
          // Alocação do novo titulo da consolidação no objeto de cores
          colorsCopy[title] = colorsCopy[highestPercentualMeta.ref];
          // Deletamos essas entradas da copia do objeto de cores
          currentConsEnunciados.forEach((en) => delete colorsCopy[en]);
        }

        // reset
        highestPercentualMeta.total = 0;
        highestPercentualMeta.ref = null;
      }

      if (JSON.stringify(colorsCopy) !== JSON.stringify(colorsPayload)) {
        return colorsCopy;
      }
    }
    return colorsPayload;
  }

  isEvolutiveCard() {
    return this._cardType === EVOLUTIVE_CARD;
  }

  isEvolutiveCrossCard() {
    return this._cardType === EVOLUTIVE_CROSS_CARD;
  }

  isBarChart() {
    return this._chartType === BAR;
  }

  isPieChart() {
    return this._chartType === PIE;
  }

}
